import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Award, 
  Clock, 
  CheckCircle, 
  Play,
  TrendingUp,
  Target,
  Calendar,
  BarChart3,
  Trophy,
  Flame,
  Star
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { curriculum } from '../data/mock';
import { moduleContent } from '../data/moduleContent';
import { getUserLessonProgress, getUserProgress, getUserCertificate, getEnrollmentInfo } from '../services/api';
import CertificateCountdown from './CertificateCountdown';

const UserDashboard = ({ userId, userName, userEmail }) => {
  const [lessonProgress, setLessonProgress] = useState([]);
  const [moduleProgress, setModuleProgress] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [enrollmentInfo, setEnrollmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const [lessons, modules, cert, enrollData] = await Promise.all([
          getUserLessonProgress(userId).catch(() => []),
          getUserProgress(userId).catch(() => []),
          getUserCertificate(userId).catch(() => null),
          getEnrollmentInfo(userId).catch(() => null)
        ]);
        
        setLessonProgress(lessons || []);
        setModuleProgress(modules || []);
        setCertificate(cert);
        setEnrollmentInfo(enrollData);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId]);

  // Calculate stats
  const getTotalLessons = () => {
    return Object.values(moduleContent).reduce((sum, mod) => sum + (mod.lessons?.length || 0), 0);
  };

  const getModuleLessonsCompleted = (moduleId) => {
    return lessonProgress.filter(lp => lp.module_id === moduleId).length;
  };

  const getModuleLessonCount = (moduleId) => {
    return moduleContent[moduleId]?.lessons?.length || 0;
  };

  const totalLessons = getTotalLessons();
  const completedLessons = lessonProgress.length;
  const overallProgress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  const completedModules = moduleProgress.filter(p => p.completed).length;

  // Calculate time spent (estimate based on lessons completed)
  const estimatedTimeSpent = completedLessons * 45; // 45 min per lesson avg
  const hoursSpent = Math.floor(estimatedTimeSpent / 60);
  const minutesSpent = estimatedTimeSpent % 60;

  // Get achievements
  const achievements = [
    { id: 'first_lesson', name: 'First Step', desc: 'Complete your first lesson', unlocked: completedLessons >= 1 },
    { id: 'module_1', name: 'Safety Starter', desc: 'Complete Module 1', unlocked: getModuleLessonsCompleted(1) >= getModuleLessonCount(1) },
    { id: 'five_lessons', name: 'Quick Learner', desc: 'Complete 5 lessons', unlocked: completedLessons >= 5 },
    { id: 'half_way', name: 'Halfway Hero', desc: 'Complete 50% of course', unlocked: overallProgress >= 50 },
    { id: 'all_modules', name: 'Safety Champion', desc: 'Complete all modules', unlocked: overallProgress >= 100 },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Please Sign In</h2>
          <p className="text-slate-600 mb-4">Sign in to view your progress dashboard</p>
          <Link to="/">
            <Button className="bg-emerald-500 hover:bg-emerald-600">Go to Course</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Course</span>
              </Link>
              <div className="h-6 w-px bg-slate-300 hidden sm:block" />
              <h1 className="text-xl font-bold text-slate-900">My Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{userName || 'Student'}</p>
                <p className="text-xs text-slate-500">{userEmail}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold">
                {userName?.charAt(0) || 'S'}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Hero Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{Math.round(overallProgress)}%</span>
            </div>
            <p className="text-emerald-100 text-sm">Course Progress</p>
            <Progress value={overallProgress} className="h-2 bg-white/20 mt-2" />
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{completedLessons}/{totalLessons}</p>
                <p className="text-sm text-slate-500">Lessons Completed</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{hoursSpent}h {minutesSpent}m</p>
                <p className="text-sm text-slate-500">Time Invested</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                certificate ? 'bg-yellow-100' : 'bg-slate-100'
              }`}>
                <Award className={`w-6 h-6 ${certificate ? 'text-yellow-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{certificate ? '1' : '0'}</p>
                <p className="text-sm text-slate-500">Certificate{certificate ? '' : 's'}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Certificate Countdown Timer */}
        {enrollmentInfo && overallProgress < 100 && (
          <div className="mb-8">
            <CertificateCountdown 
              enrollmentDate={enrollmentInfo.enrollment_date}
              daysAllowed={enrollmentInfo.days_allowed || 30}
              overallProgress={overallProgress}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {['overview', 'modules', 'achievements'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab
                  ? 'text-emerald-600 border-emerald-500'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Module Progress */}
            <Card className="p-6 lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-500" />
                Module Progress
              </h3>
              <div className="space-y-4">
                {curriculum.map((module) => {
                  const lessonCount = getModuleLessonCount(module.id);
                  const lessonsCompleted = getModuleLessonsCompleted(module.id);
                  const pct = lessonCount > 0 ? (lessonsCompleted / lessonCount) * 100 : 0;
                  
                  return (
                    <div key={module.id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-700">Module {module.id}: {module.title.split(':')[0]}</span>
                        <span className="text-xs text-slate-500">{lessonsCompleted}/{lessonCount}</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-500" />
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Link to="/module/1" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Play className="w-4 h-4 mr-2" />
                    Continue Learning
                  </Button>
                </Link>
                <Link to="/#curriculum" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <BookOpen className="w-4 h-4 mr-2" />
                    View Curriculum
                  </Button>
                </Link>
                {certificate && (
                  <Link to={`/verify/${certificate.certificate_number}`} className="block">
                    <Button variant="outline" className="w-full justify-start">
                      <Award className="w-4 h-4 mr-2" />
                      View Certificate
                    </Button>
                  </Link>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'modules' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {curriculum.map((module) => {
              const lessonCount = getModuleLessonCount(module.id);
              const lessonsCompleted = getModuleLessonsCompleted(module.id);
              const pct = lessonCount > 0 ? (lessonsCompleted / lessonCount) * 100 : 0;
              const isComplete = pct >= 100;
              
              return (
                <Link key={module.id} to={`/module/${module.id}`}>
                  <Card className={`p-5 h-full hover:shadow-md transition-shadow ${
                    isComplete ? 'border-emerald-200 bg-emerald-50' : ''
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isComplete ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {isComplete ? <CheckCircle className="w-5 h-5" /> : module.id}
                      </div>
                      <span className={`text-xs font-medium ${isComplete ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {Math.round(pct)}%
                      </span>
                    </div>
                    <h4 className="font-medium text-slate-900 text-sm mb-1 line-clamp-2">{module.title}</h4>
                    <p className="text-xs text-slate-500">{lessonsCompleted}/{lessonCount} lessons</p>
                    <Progress value={pct} className="h-1.5 mt-3" />
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((achievement) => (
              <Card 
                key={achievement.id} 
                className={`p-5 ${achievement.unlocked ? 'border-yellow-200 bg-yellow-50' : 'opacity-60'}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    achievement.unlocked ? 'bg-yellow-400' : 'bg-slate-200'
                  }`}>
                    <Star className={`w-6 h-6 ${achievement.unlocked ? 'text-yellow-900' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{achievement.name}</h4>
                    <p className="text-sm text-slate-500">{achievement.desc}</p>
                  </div>
                </div>
                {achievement.unlocked && (
                  <div className="mt-3 text-xs text-yellow-700 font-medium">
                    ✓ Unlocked
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
